let audioCtx: AudioContext | null = null;
const IOS = typeof navigator !== 'undefined' && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
let idleTimer = 0;
export type CallTone = 'standard' | 'surprise';
export function chooseCallTone(random:()=>number=Math.random):CallTone { return random()<0.001?'surprise':'standard'; }

function getCtx(): AudioContext | null {
  try {
    audioCtx ??= new AudioContext();
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume();
    }
    // iOS: a running Web Audio context next to a WebRTC call makes call audio stutter, so pause it when idle.
    if (IOS) { clearTimeout(idleTimer); const ctx = audioCtx; idleTimer = window.setTimeout(() => { if (ctx.state === 'running') void ctx.suspend(); }, 4000); }
    return audioCtx;
  } catch {
    return null;
  }
}

export function isSoundAllowed(userStatus?: string): boolean {
  if (userStatus === 'dnd') return false;
  try {
    if (localStorage.getItem('malvo-notif-sound') === '0') return false;
  } catch {}
  return true;
}

function playNotes(
  notes: { freq: number; start: number; dur: number; gain?: number; type?: OscillatorType }[],
  userStatus?: string,
  ignoreDnd = false
) {
  if (!ignoreDnd && !isSoundAllowed(userStatus)) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  for (const n of notes) {
    try {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = n.type || 'sine';
      osc.frequency.setValueAtTime(n.freq, now + n.start);
      const peak = n.gain ?? 0.06;
      g.gain.setValueAtTime(0.0001, now + n.start);
      g.gain.exponentialRampToValueAtTime(peak, now + n.start + 0.018);
      g.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + n.start);
      osc.stop(now + n.start + n.dur + 0.02);
    } catch {}
  }
}

export function playCallJoinSound(userStatus?: string) {
  playNotes(
    [
      { freq: 440, start: 0, dur: 0.11, gain: 0.055, type: 'sine' },
      { freq: 587.33, start: 0.085, dur: 0.16, gain: 0.065, type: 'sine' }
    ],
    userStatus,
    true
  );
}

export function playCallLeaveSound(userStatus?: string) {
  playNotes(
    [
      { freq: 523.25, start: 0, dur: 0.1, gain: 0.05, type: 'sine' },
      { freq: 349.23, start: 0.08, dur: 0.16, gain: 0.055, type: 'sine' }
    ],
    userStatus,
    true
  );
}

export function playMessageSound(userStatus?: string) {
  playNotes(
    [
      { freq: 587.33, start: 0, dur: 0.09, gain: 0.045, type: 'sine' },
      { freq: 880, start: 0.065, dur: 0.14, gain: 0.05, type: 'sine' }
    ],
    userStatus,
    false
  );
}

// Soft bell voice: sine body + quiet octave shimmer through a low-pass and a short echo, so tones sound round instead of beepy.
function playBells(notes: { freq: number; at: number; dur: number; gain: number }[], userStatus?: string, ignoreDnd = false) {
  if (!ignoreDnd && !isSoundAllowed(userStatus)) return;
  const ctx = getCtx();
  if (!ctx) return;
  const oscillators: OscillatorNode[] = [];
  const nodes: AudioNode[] = [];
  const t0 = ctx.currentTime + 0.01;
  const out = ctx.createGain();
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 3200;
  const echo = ctx.createDelay(0.4);
  echo.delayTime.value = 0.16;
  const fb = ctx.createGain();
  fb.gain.value = 0.18;
  nodes.push(out, lp, echo, fb);
  out.connect(lp);
  lp.connect(ctx.destination);
  lp.connect(echo);
  echo.connect(fb);
  fb.connect(echo);
  echo.connect(ctx.destination);
  for (const n of notes) {
    for (const [mult, share, type] of [[1, 1, 'sine'], [2, 0.18, 'sine'], [3, 0.06, 'triangle']] as [number, number, OscillatorType][]) {
      try {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        oscillators.push(o);
        nodes.push(o, g);
        o.type = type;
        o.frequency.value = n.freq * mult;
        g.gain.setValueAtTime(0.0001, t0 + n.at);
        g.gain.exponentialRampToValueAtTime(n.gain * share, t0 + n.at + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.dur);
        o.connect(g);
        g.connect(out);
        o.start(t0 + n.at);
        o.stop(t0 + n.at + n.dur + 0.05);
      } catch {}
    }
  }
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(cleanup);
    for (const oscillator of oscillators) { try { oscillator.stop(); } catch {} }
    for (const node of nodes) { try { node.disconnect(); } catch {} }
  };
  const cleanup = setTimeout(stop, (Math.max(...notes.map(n => n.at + n.dur)) + 1) * 1000);
  return stop;
}

let outgoingRingStop: (() => void) | undefined;
export function stopOutgoingRingtone() {
  outgoingRingStop?.();
  outgoingRingStop = undefined;
}

// Outgoing call ringback: two gentle bell pairs, repeated by the caller every few seconds.
export function playOutgoingRingPulse(userStatus?: string,tone:CallTone='standard') {
  stopOutgoingRingtone();
  if (localStorage.getItem('malvo-outgoing-ring') === '0') return;
  outgoingRingStop = playBells(
    tone==='surprise'?[
      {freq:523.25,at:0,dur:.28,gain:.05},{freq:659.25,at:.18,dur:.3,gain:.05},
      {freq:783.99,at:.36,dur:.3,gain:.05},{freq:1046.5,at:.62,dur:.5,gain:.05},
      {freq:783.99,at:1.12,dur:.25,gain:.04},{freq:1046.5,at:1.35,dur:.45,gain:.045}
    ]:[
      { freq: 659.25, at: 0, dur: 0.9, gain: 0.06 },
      { freq: 830.61, at: 0.22, dur: 1.1, gain: 0.055 },
      { freq: 659.25, at: 1.1, dur: 0.9, gain: 0.05 },
      { freq: 830.61, at: 1.32, dur: 1.2, gain: 0.045 }
    ],
    userStatus
  );
}

// Screen share started: quick rising pair; stopped: falling pair with a soft tail.
export function playScreenShareStartSound(userStatus?: string) {
  playBells([{ freq: 587.33, at: 0, dur: 0.35, gain: 0.06 }, { freq: 880, at: 0.09, dur: 0.5, gain: 0.06 }], userStatus, true);
}
export function playScreenShareStopSound(userStatus?: string) {
  playBells([{ freq: 880, at: 0, dur: 0.3, gain: 0.055 }, { freq: 659.25, at: 0.09, dur: 0.35, gain: 0.05 }, { freq: 440, at: 0.19, dur: 0.6, gain: 0.05 }], userStatus, true);
}

export function playGameMoveSound() {
  playNotes([{ freq: 320, start: 0, dur: 0.06, gain: 0.045, type: 'triangle' }], undefined, true);
}

export function playGameCaptureSound() {
  playNotes(
    [
      { freq: 240, start: 0, dur: 0.05, gain: 0.055, type: 'triangle' },
      { freq: 480, start: 0.04, dur: 0.09, gain: 0.05, type: 'sine' }
    ],
    undefined,
    true
  );
}

export function playGameWinSound() {
  playNotes(
    [
      { freq: 523.25, start: 0, dur: 0.12, gain: 0.05, type: 'sine' },
      { freq: 659.25, start: 0.1, dur: 0.12, gain: 0.055, type: 'sine' },
      { freq: 783.99, start: 0.2, dur: 0.14, gain: 0.06, type: 'sine' },
      { freq: 1046.5, start: 0.32, dur: 0.28, gain: 0.07, type: 'sine' }
    ],
    undefined,
    true
  );
}

export function playMuteToggleSound(isMuted: boolean) {
  if (isMuted) {
    // Clean descending double-pop for Microphone Mute
    playNotes(
      [
        { freq: 540, start: 0, dur: 0.075, gain: 0.06, type: 'sine' },
        { freq: 380, start: 0.065, dur: 0.11, gain: 0.065, type: 'sine' }
      ],
      undefined,
      true
    );
  } else {
    // Clean ascending double-pop for Microphone Unmute
    playNotes(
      [
        { freq: 400, start: 0, dur: 0.07, gain: 0.06, type: 'sine' },
        { freq: 620, start: 0.065, dur: 0.11, gain: 0.065, type: 'sine' }
      ],
      undefined,
      true
    );
  }
}

export function playDeafenToggleSound(isDeafened: boolean) {
  if (isDeafened) {
    // Rich three-tone descending chime for Full-Mute / Deafen
    playNotes(
      [
        { freq: 587.33, start: 0, dur: 0.075, gain: 0.06, type: 'sine' },
        { freq: 440, start: 0.065, dur: 0.08, gain: 0.06, type: 'sine' },
        { freq: 311.13, start: 0.135, dur: 0.14, gain: 0.07, type: 'sine' }
      ],
      undefined,
      true
    );
  } else {
    // Rich three-tone ascending chime for Undeafen
    playNotes(
      [
        { freq: 329.63, start: 0, dur: 0.075, gain: 0.06, type: 'sine' },
        { freq: 493.88, start: 0.065, dur: 0.08, gain: 0.06, type: 'sine' },
        { freq: 659.25, start: 0.135, dur: 0.14, gain: 0.07, type: 'sine' }
      ],
      undefined,
      true
    );
  }
}

export function playMentionSound(userStatus?: string) {
  playNotes(
    [
      { freq: 1046.5, start: 0, dur: 0.12, gain: 0.06, type: 'triangle' },
      { freq: 1567.98, start: 0.08, dur: 0.22, gain: 0.055, type: 'sine' },
      { freq: 2093, start: 0.08, dur: 0.12, gain: 0.015, type: 'sine' }
    ],
    userStatus,
    false
  );
}

// Incoming call: a bright marimba-like phrase (~2.4 s) with a soft echo, meant to loop every ~3.2 s.
export function scheduleRingPhrase(ctx: AudioContext, sink: { o: OscillatorNode; g: GainNode }[],tone:CallTone='standard') {
  const t0 = ctx.currentTime + 0.02;
  const master = ctx.createGain();
  master.gain.value = 1.2;
  const echo = ctx.createDelay(0.5);
  echo.delayTime.value = 0.21;
  const fb = ctx.createGain();
  fb.gain.value = 0.28;
  master.connect(ctx.destination);
  master.connect(echo);
  echo.connect(fb);
  fb.connect(echo);
  echo.connect(ctx.destination);
  const notes: [number, number][] = tone==='surprise'?[
    [523.25,0],[659.25,.16],[783.99,.32],[1046.5,.56],[783.99,.95],[659.25,1.12],[1046.5,1.4]
  ]:[
    [659.25, 0], [987.77, 0.15], [830.61, 0.3], [1318.51, 0.45],
    [1244.51, 0.9], [987.77, 1.05], [830.61, 1.2], [987.77, 1.35], [1318.51, 1.65]
  ];
  for (const [freq, at] of notes) {
    for (const [mult, peak, decay, type] of [[1, 0.07, 0.55, 'sine'], [4, 0.012, 0.12, 'sine'], [2, 0.018, 0.3, 'triangle']] as [number, number, number, OscillatorType][]) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq * mult;
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + decay);
      o.connect(g);
      g.connect(master);
      o.start(t0 + at);
      o.stop(t0 + at + decay + 0.05);
      sink.push({ o, g });
    }
  }
  // keep the echo nodes collectable once the phrase ends
  const stopEcho = () => { clearTimeout(cleanup); try { master.disconnect(); echo.disconnect(); fb.disconnect(); } catch {} };
  const cleanup = setTimeout(stopEcho, 3200);
  return stopEcho;
}

// Original synthesized hiss/burst. No game sound assets or remote audio requests.
export function playCreeperEffectSound(userStatus?:string){
  if(!isSoundAllowed(userStatus))return;
  const ctx=getCtx();if(!ctx)return;
  const sources:AudioBufferSourceNode[]=[],nodes:AudioNode[]=[];let stopped=false;
  const stop=()=>{if(stopped)return;stopped=true;clearTimeout(timer);for(const s of sources)try{s.stop();}catch{}for(const n of nodes)n.disconnect();};
  const timer=setTimeout(stop,1100);
  try{
    for(const [offset,duration,frequency,peak,type] of [[0,.18,2400,.025,'highpass'],[.19,.55,680,.085,'lowpass']] as [number,number,number,number,BiquadFilterType][]){
      const buffer=ctx.createBuffer(1,Math.ceil(ctx.sampleRate*duration),ctx.sampleRate),samples=buffer.getChannelData(0);
      for(let i=0;i<samples.length;i++)samples[i]=(Math.random()*2-1)*Math.pow(1-i/samples.length,1.5);
      const source=ctx.createBufferSource(),filter=ctx.createBiquadFilter(),gain=ctx.createGain();sources.push(source);nodes.push(source,filter,gain);source.buffer=buffer;filter.type=type;filter.frequency.value=frequency;gain.gain.setValueAtTime(.0001,ctx.currentTime+offset);gain.gain.exponentialRampToValueAtTime(peak,ctx.currentTime+offset+.015);gain.gain.exponentialRampToValueAtTime(.0001,ctx.currentTime+offset+duration);source.connect(filter);filter.connect(gain);gain.connect(ctx.destination);source.start(ctx.currentTime+offset);source.stop(ctx.currentTime+offset+duration);
    }
  }catch{stop();}
  return stop;
}
